import React, { useState, useRef, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";

interface MagnifiableImageProps {
  imageSrc: string;
  alt?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

const ZOOM_LEVEL = 3;
const MAGNIFIER_SIZE = 200;

// Custom dot cursor SVG - small circle with white border for visibility
const DOT_CURSOR = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="3" fill="rgba(0,0,0,0.6)" stroke="white" stroke-width="1.5"/></svg>') 8 8, crosshair`;

export const MagnifiableImage: React.FC<MagnifiableImageProps> = ({
  imageSrc,
  alt = "Transaction image",
  style,
  onLoad,
  onError,
}) => {
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detect mobile devices (will open in new tab instead of magnifying)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const calculateImageDimensions = useCallback((): boolean => {
    if (!imageRef.current) {
      return false;
    }

    const img = imageRef.current;
    const containerWidth = img.offsetWidth;
    const containerHeight = img.offsetHeight;

    // Validation: Container must be sized
    if (!containerWidth || !containerHeight) {
      console.debug("invalid container dimensions at image load", {
        containerWidth,
        containerHeight,
        imageSrc,
      });
      return false;
    }

    // Validation: Natural dimensions must exist
    if (!img.naturalWidth || !img.naturalHeight) {
      console.debug("invalid natural image dimensions", {
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
        imageSrc,
      });
      return false;
    }

    const naturalAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerWidth / containerHeight;

    // Validation: Aspect ratios must be finite
    if (!isFinite(naturalAspect) || !isFinite(containerAspect)) {
      console.debug("invalid aspect ratios calculated", {
        naturalAspect,
        containerAspect,
        imageSrc,
      });
      return false;
    }

    let renderedWidth: number;
    let renderedHeight: number;
    let offsetX: number;
    let offsetY: number;

    if (naturalAspect > containerAspect) {
      // Image is wider than container - fits to width (letterboxed vertically)
      renderedWidth = containerWidth;
      renderedHeight = containerWidth / naturalAspect;
      offsetX = 0;
      offsetY = (containerHeight - renderedHeight) / 2;
    } else {
      // Image is taller than container - fits to height (pillarboxed horizontally)
      renderedHeight = containerHeight;
      renderedWidth = containerHeight * naturalAspect;
      offsetX = (containerWidth - renderedWidth) / 2;
      offsetY = 0;
    }

    // Validation: Calculated dimensions must be valid and positive
    if (!isFinite(renderedWidth) || !isFinite(renderedHeight) || renderedWidth <= 0 || renderedHeight <= 0) {
      console.debug("invalid calculated dimensions", {
        renderedWidth,
        renderedHeight,
        imageSrc,
      });
      return false;
    }

    setImageDimensions({
      width: renderedWidth,
      height: renderedHeight,
      offsetX,
      offsetY,
    });

    return true;
  }, [imageSrc]);

  useEffect(() => {
    if (isMobile) {
      // Don't set up magnifier on mobile
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!imageRef.current || !containerRef.current) {
        return;
      }

      // Get image bounding rect to calculate coordinates relative to the actual image
      const imageRect = imageRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Calculate coordinates relative to the rendered image content (accounting for object-fit: contain)
      const x = e.clientX - imageRect.left - imageDimensions.offsetX;
      const y = e.clientY - imageRect.top - imageDimensions.offsetY;

      // Only show magnifier if cursor is within image bounds
      if (x < 0 || x > imageDimensions.width || y < 0 || y > imageDimensions.height) {
        setShowMagnifier(false);
        return;
      }

      // Ensure magnifier is visible when over the image
      setShowMagnifier(true);

      // Calculate magnifier position relative to container (for visibility)
      const containerX = e.clientX - containerRect.left;
      const containerY = e.clientY - containerRect.top;
      const magnifierX = containerX - MAGNIFIER_SIZE / 2;
      const magnifierY = containerY - MAGNIFIER_SIZE / 2;

      // Calculate background position using image-relative coordinates
      const bgX = -(x * ZOOM_LEVEL - MAGNIFIER_SIZE / 2);
      const bgY = -(y * ZOOM_LEVEL - MAGNIFIER_SIZE / 2);

      setMagnifierPosition({ x: magnifierX, y: magnifierY });
      setBackgroundPosition({ x: bgX, y: bgY });
    };

    const handleMouseEnter = () => {
      // If dimensions weren't calculated (image was hidden when it loaded), try now
      if (imageDimensions.width === 0) {
        calculateImageDimensions();
      }
      setShowMagnifier(true);
    };

    const handleMouseLeave = () => {
      setShowMagnifier(false);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [isMobile, imageDimensions, calculateImageDimensions]);

  const handleImageLoad = () => {
    // Call onLoad immediately to clear the loading skeleton
    if (onLoad) {
      onLoad();
    }

    calculateImageDimensions();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        cursor: isMobile ? "default" : DOT_CURSOR,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        ref={imageRef}
        src={imageSrc}
        alt={alt}
        style={{
          ...style,
          display: "block",
        }}
        onLoad={handleImageLoad}
        onError={onError}
        draggable={false}
      />
      {/* Magnifier (desktop only) */}
      {!isMobile && showMagnifier && imageDimensions.width > 0 && (
        <Box
          sx={{
            position: "absolute",
            pointerEvents: "none",
            border: "2px solid rgba(255, 255, 255, 0.8)",
            borderRadius: "50%",
            boxShadow: "0 8px 24px -4px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1)",
            zIndex: 1000,
            width: MAGNIFIER_SIZE,
            height: MAGNIFIER_SIZE,
            left: magnifierPosition.x,
            top: magnifierPosition.y,
            backgroundImage: `url(${imageSrc})`,
            backgroundSize: `${imageDimensions.width * ZOOM_LEVEL}px ${imageDimensions.height * ZOOM_LEVEL}px`,
            backgroundPosition: `${backgroundPosition.x}px ${backgroundPosition.y}px`,
            backgroundRepeat: "no-repeat",
          }}
        />
      )}
    </Box>
  );
};

export default MagnifiableImage;
